/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import "./seti.css";

/** Provide the icon for a given file type. */
export const FileIcon = ({ filePath }: { filePath: string }) => {
  const index = filePath.lastIndexOf(".");

  if (index === -1)
    return <span className="seti seti-default"></span>

  const extension = filePath.substring(filePath.lastIndexOf("."), filePath.length).toLowerCase();

  switch (extension) {
    case ".json": return <span className="seti seti-json"></span>;
    case ".css": return <span className="seti seti-css"></span>;
    case ".css.map": return <span className="seti seti-css"></span>;
    case ".csv": return <span className="seti seti-csv"></span>;
    case ".xls": return <span className="seti seti-xls"></span>;
    case ".xlsx": return <span className="seti seti-xls"></span>;
    case ".doc": return <span className="seti seti-word"></span>;
    case ".docx": return <span className="seti seti-word"></span>;
    case ".ico": return <span className="seti seti-favicon"></span>;
    case ".gitignore": return <span className="seti seti-git"></span>;
    case ".gitconfig": return <span className="seti seti-git"></span>;
    case ".gitkeep": return <span className="seti seti-git"></span>;
    case ".gitattributes": return <span className="seti seti-git"></span>;
    case ".gitmodules": return <span className="seti seti-git"></span>;
    case ".gql": return <span className="seti seti-graphql"></span>;
    case ".graphql": return <span className="seti seti-graphql"></span>;
    case ".js": return <span className="seti seti-javascript"></span>;
    case ".js.map": return <span className="seti seti-javascript"></span>;
    case ".spec.js": return <span className="seti seti-javascript_1"></span>;
    case ".test.js": return <span className="seti seti-javascript_1"></span>;
    case ".es": return <span className="seti seti-javascript"></span>;
    case ".es5": return <span className="seti seti-javascript"></span>;
    case ".es7": return <span className="seti seti-javascript"></span>;
    case ".npm-debug.log": return <span className="seti seti-npm"></span>;
    case ".npmignore": return <span className="seti seti-npm_1"></span>;
    case ".jsx": return <span className="seti seti-react"></span>;
    case ".spec.jsx": return <span className="seti seti-react_1"></span>;
    case ".test.jsx": return <span className="seti seti-react_1"></span>;
    case ".cjsx": return <span className="seti seti-react"></span>;
    case ".tsx": return <span className="seti seti-react"></span>;
    case ".spec.tsx": return <span className="seti seti-react_2"></span>;
    case ".test.tsx": return <span className="seti seti-react_2"></span>;
    case ".sass": return <span className="seti seti-sass"></span>;
    case ".scss": return <span className="seti seti-sass"></span>;
    case ".ts": return <span className="seti seti-typescript"></span>;
    case ".spec.ts": return <span className="seti seti-typescript_1"></span>;
    case ".test.ts": return <span className="seti seti-typescript_1"></span>;
    case ".wasm": return <span className="seti seti-wasm"></span>;
    case ".ai": return <span className="seti seti-illustrator"></span>;
    case ".psd": return <span className="seti seti-photoshop"></span>;
    case ".pdf": return <span className="seti seti-pdf"></span>;
    case ".eot": return <span className="seti seti-font"></span>;
    case ".ttf": return <span className="seti seti-font"></span>;
    case ".woff": return <span className="seti seti-font"></span>;
    case ".woff2": return <span className="seti seti-font"></span>;
    case ".gif": return <span className="seti seti-image"></span>;
    case ".jpg": return <span className="seti seti-image"></span>;
    case ".jpeg": return <span className="seti seti-image"></span>;
    case ".png": return <span className="seti seti-image"></span>;
    case ".pxm": return <span className="seti seti-image"></span>;
    case ".svg": return <span className="seti seti-svg"></span>;
    case ".svgx": return <span className="seti seti-image"></span>;
    case ".mov": return <span className="seti seti-video"></span>;
    case ".ogv": return <span className="seti seti-video"></span>;
    case ".webm": return <span className="seti seti-video"></span>;
    case ".avi": return <span className="seti seti-video"></span>;
    case ".mpg": return <span className="seti seti-video"></span>;
    case ".mp4": return <span className="seti seti-video"></span>;
    case ".mp3": return <span className="seti seti-audio"></span>;
    case ".ogg": return <span className="seti seti-audio"></span>;
    case ".wav": return <span className="seti seti-audio"></span>;
    case ".flac": return <span className="seti seti-audio"></span>;
    case ".3ds": return <span className="seti seti-svg_1"></span>;
    case ".3dm": return <span className="seti seti-svg_1"></span>;
    case ".stl": return <span className="seti seti-svg_1"></span>;
    case ".obj": return <span className="seti seti-svg_1"></span>;
    case ".dae": return <span className="seti seti-svg_1"></span>;
    case ".eslintrc": return <span className="seti seti-eslint"></span>;
    case ".eslintrc.js": return <span className="seti seti-eslint"></span>;
    case ".eslintrc.yaml": return <span className="seti seti-eslint"></span>;
    case ".eslintrc.yml": return <span className="seti seti-eslint"></span>;
    case ".eslintrc.json": return <span className="seti seti-eslint"></span>;
    case ".eslintignore": return <span className="seti seti-eslint_1"></span>;
    case ".firebaserc": return <span className="seti seti-firebase"></span>;
    case ".jshintrc": return <span className="seti seti-javascript_2"></span>;
    case ".jscsrc": return <span className="seti seti-javascript_2"></span>;
    case ".stylelintrc": return <span className="seti seti-stylelint"></span>;
    case ".stylelintrc.json": return <span className="seti seti-stylelint"></span>;
    case ".stylelintrc.yaml": return <span className="seti seti-stylelint"></span>;
    case ".stylelintrc.yml": return <span className="seti seti-stylelint"></span>;
    case ".stylelintrc.js": return <span className="seti seti-stylelint"></span>;
    case ".stylelintignore": return <span className="seti seti-stylelint_1"></span>;
    case ".tmp": return <span className="seti seti-clock_1"></span>;
    case ".htaccess": return <span className="seti seti-config"></span>;
    case ".key": return <span className="seti seti-lock"></span>;
    case ".cert": return <span className="seti seti-lock"></span>;
    case ".ds_store": return <span className="seti seti-ignored"></span>;
    case ".md": return <span className="seti seti-info"></span>;
    default: return <span className="seti seti-default"></span>;
  }
}